import { useState } from 'react';
import Router from 'next/router';
import { uuidv4 } from '@soundxyz/utils';

import { CreditRoleEnum } from '@soundxyz/utils/const';
import {
  ERROR_TYPE,
  PILLARS,
  UPLOAD_RELEASE_ERROR_ACTIONS,
} from '@soundxyz/utils/constants/logging';
import { uploadToolInitialPersistState } from '@components/Dashboard/upload-new/store/initial-states';
import {
  getUploadToolPersistence,
  uploadToolState,
} from '@components/Dashboard/upload-new/store/state';
import { useStableCallback } from '@hooks/useStableCallback';
import { RELEASE_INFO_STEP_ID } from '@utils/constants';
import { getDraftEditButtonUrlV2 } from '@utils/helpers';
import { useAuthArtist } from './useAuthArtist';
import { useLogError } from './useLogErrorNew';

export function useTieredUpload() {
  const { authArtist, authUser } = useAuthArtist();
  const logError = useLogError();

  const [loading, setLoading] = useState(false);

  const createDraft = useStableCallback(async () => {
    if (!authArtist?.id || !authUser?.publicAddress) return;

    try {
      setLoading(true);

      const id = uuidv4();

      // We set the owner address to the Splits & Credits from the beginning
      await getUploadToolPersistence({ id }).set({
        ...uploadToolInitialPersistState(),
        id,
        artistId: authArtist.id,
        splits: {
          fields: {
            splitContractAddress: null,
            splits: [
              {
                ownerAddress: authUser.publicAddress,
                roles: [CreditRoleEnum.ARTIST],
                percent: '100',
              },
            ],
          },
        },
      });

      uploadToolState.transaction = null;
      uploadToolState.releaseId = null;
      uploadToolState.expectedContractAddress = null;

      Router.replace(
        getDraftEditButtonUrlV2({
          draftId: id,
          stepId: RELEASE_INFO_STEP_ID,
        }),
      ).finally(() => setLoading(false));
    } catch (error) {
      const errorMessage = "Couldn't create draft to local storage";
      logError({
        action: UPLOAD_RELEASE_ERROR_ACTIONS.RELEASE_UPLOAD_CREATE_DRAFT_FAILED,
        error: error,
        errorType: ERROR_TYPE.LOCAL_STORAGE,
        message: errorMessage,
        level: 'fatal',
        isArtistOnlyPage: true,
        pillar: PILLARS.UPLOAD_RELEASE,
      });
      setLoading(false);
    }
  });

  return { loading, createDraft };
}
